import React, { useEffect, useState } from 'react';
import { CheckCircle2, ClipboardList, Clock, Mail, UserRound } from 'lucide-react';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/ui-kit/PageBits';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

function formatDate(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('es-MX', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return value;
  }
}

export default function AdminTasks() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);

  const loadTasks = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/tasks');
      setTasks(response.data || []);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'No se pudieron cargar las tareas.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTasks();
  }, []);

  const claimTask = async (task) => {
    setProcessingId(task.id);
    try {
      await api.post(`/admin/tasks/${task.id}/claim`);
      toast.success('Tarea asignada correctamente.');
      await loadTasks();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'No se pudo tomar la tarea.');
    } finally {
      setProcessingId(null);
    }
  };

  const completeTask = async (task) => {
    if (!window.confirm('¿Confirmas que esta tarea ya fue atendida?')) return;

    setProcessingId(task.id);
    try {
      await api.post(`/admin/tasks/${task.id}/complete`);
      toast.success('Tarea completada.');
      await loadTasks();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'No se pudo completar la tarea.');
    } finally {
      setProcessingId(null);
    }
  };

  const pending = tasks.filter((task) => task.status === 'pending').length;
  const inProgress = tasks.filter((task) => task.status === 'in_progress').length;
  const completed = tasks.filter((task) => task.status === 'completed').length;

  return (
    <div className="min-h-full bg-[#F5F7FB]">
      <PageHeader
        title="Seguimiento de alumnos"
        description="Atiende las tareas generadas después de la graduación de un módulo."
      />

      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-2xl border p-5">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-amber-100 text-amber-700"><Clock size={20} /></div>
              <div><p className="text-sm text-slate-500">Pendientes</p><p className="text-2xl font-bold text-slate-900">{pending}</p></div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border p-5">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-blue-100 text-blue-700"><ClipboardList size={20} /></div>
              <div><p className="text-sm text-slate-500">En progreso</p><p className="text-2xl font-bold text-slate-900">{inProgress}</p></div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border p-5">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-emerald-100 text-emerald-700"><CheckCircle2 size={20} /></div>
              <div><p className="text-sm text-slate-500">Completadas</p><p className="text-2xl font-bold text-slate-900">{completed}</p></div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="bg-white rounded-2xl border p-10 text-center text-slate-500">
            Cargando tareas...
          </div>
        ) : tasks.length === 0 ? (
          <div className="bg-white rounded-2xl border p-12 text-center">
            <ClipboardList className="mx-auto mb-4 text-slate-400" size={42} />
            <h2 className="text-lg font-semibold text-slate-800">No hay tareas de seguimiento</h2>
            <p className="mt-1 text-sm text-slate-500">
              Aquí aparecerán las tareas generadas después de aprobar módulos.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {tasks.map((task) => (
              <div key={task.id} className="bg-white rounded-2xl border p-5 shadow-sm">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <h2 className="font-semibold text-slate-900">{task.title}</h2>
                      <Badge variant={task.status === 'completed' ? 'default' : task.status === 'in_progress' ? 'secondary' : 'outline'}>
                        {task.status === 'completed' ? 'Completada' : task.status === 'in_progress' ? 'En progreso' : 'Pendiente'}
                      </Badge>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                      <div className="flex items-center gap-2 text-slate-700">
                        <UserRound size={16} className="text-slate-400" />
                        <span><strong>Alumno:</strong> {task.student_name || task.student_id}</span>
                      </div>

                      <div className="flex items-center gap-2 text-slate-700">
                        <ClipboardList size={16} className="text-slate-400" />
                        <span>
                          <strong>Módulo:</strong> {task.module_slot}
                          {task.next_module_name ? ` → ${task.next_module_name}` : ''}
                        </span>
                      </div>

                      {task.student_email && (
                        <div className="flex items-center gap-2 text-slate-700">
                          <Mail size={16} className="text-slate-400" />
                          <span>{task.student_email}</span>
                        </div>
                      )}
                    </div>

                    <p className="mt-4 text-sm leading-6 text-slate-600">{task.description}</p>

                    <p className="mt-3 text-xs text-slate-400">
                      Creada: {formatDate(task.created_at)}
                      {task.completed_at ? ` · Completada: ${formatDate(task.completed_at)}` : ''}
                    </p>
                  </div>

                  <div className="shrink-0">
                    {task.status === 'pending' && (
                      <Button onClick={() => claimTask(task)} disabled={processingId === task.id}>
                        {processingId === task.id ? 'Asignando...' : 'Tomar tarea'}
                      </Button>
                    )}

                    {task.status === 'in_progress' && (
                      <Button onClick={() => completeTask(task)} disabled={processingId === task.id}>
                        {processingId === task.id ? 'Guardando...' : 'Completar'}
                      </Button>
                    )}

                    {task.status === 'completed' && (
                      <div className="flex items-center gap-2 text-sm text-emerald-700">
                        <CheckCircle2 size={18} />
                        Completada
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
